import { useCallback, useRef, useState } from 'react';
import Editor, { OnMount, OnChange } from '@monaco-editor/react';
import type { editor, Uri } from 'monaco-editor';
import { cn } from '../../lib/utils';

export interface JsonEditorProps {
  /** Current JSON value as a string */
  value: string;
  /** Callback when value changes */
  onChange: (value: string) => void;
  /** Height of the editor (default: 200px) */
  height?: string | number;
  /** Whether the editor is disabled */
  disabled?: boolean;
  /** Placeholder text when empty */
  placeholder?: string;
  /** Additional CSS classes */
  className?: string;
  /** Error message to display */
  error?: string;
  /** Callback when validation state changes */
  onValidationChange?: (isValid: boolean, errors: string[]) => void;
}

/**
 * JSON Editor component using Monaco Editor
 *
 * Provides JSON syntax highlighting, validation, and auto-formatting.
 *
 * @example
 * ```tsx
 * <JsonEditor
 *   value={jsonString}
 *   onChange={setJsonString}
 *   height={300}
 *   onValidationChange={(isValid, errors) => {
 *     if (!isValid) console.log('Errors:', errors);
 *   }}
 * />
 * ```
 */
export function JsonEditor({
  value,
  onChange,
  height = 200,
  disabled = false,
  placeholder = '{\n  \n}',
  className,
  error,
  onValidationChange,
}: JsonEditorProps) {
  const editorRef = useRef<editor.IStandaloneCodeEditor | null>(null);
  const [internalErrors, setInternalErrors] = useState<string[]>([]);

  const handleEditorMount: OnMount = useCallback((editor, monaco) => {
    editorRef.current = editor;

    // Configure JSON language defaults
    monaco.languages.json.jsonDefaults.setDiagnosticsOptions({
      validate: true,
      schemas: [],
      allowComments: false,
      schemaValidation: 'error',
    });

    // Set up model markers listener for validation
    const model = editor.getModel();
    if (model) {
      monaco.editor.onDidChangeMarkers(([resource]: Uri[]) => {
        if (resource.toString() === model.uri.toString()) {
          const markers = monaco.editor.getModelMarkers({ resource });
          const errorMessages = markers
            .filter((marker: editor.IMarker) => marker.severity === monaco.MarkerSeverity.Error)
            .map((marker: editor.IMarker) => `Line ${marker.startLineNumber}: ${marker.message}`);

          setInternalErrors(errorMessages);
          onValidationChange?.(errorMessages.length === 0, errorMessages);
        }
      });
    }
  }, [onValidationChange]);

  const handleChange: OnChange = useCallback((newValue) => {
    onChange(newValue || '');
  }, [onChange]);

  const handleFormat = useCallback(() => {
    if (editorRef.current) {
      editorRef.current.getAction('editor.action.formatDocument')?.run();
    }
  }, []);

  // Determine if we should show placeholder
  const showPlaceholder = !value || value.trim() === '';

  return (
    <div className={cn('relative', className)}>
      <div
        className={cn(
          'rounded-md border overflow-hidden transition-colors',
          error || internalErrors.length > 0
            ? 'border-red-500 focus-within:ring-red-500'
            : 'border-input focus-within:ring-ring',
          disabled && 'opacity-50 cursor-not-allowed',
          'focus-within:ring-2 focus-within:ring-offset-2'
        )}
      >
        {showPlaceholder && !disabled && (
          <div className="absolute top-0 left-0 right-0 bottom-0 pointer-events-none z-10 flex items-start p-4 text-muted-foreground text-sm font-mono opacity-50">
            {placeholder}
          </div>
        )}
        <Editor
          height={height}
          defaultLanguage="json"
          value={value}
          onChange={handleChange}
          onMount={handleEditorMount}
          options={{
            minimap: { enabled: false },
            scrollBeyondLastLine: false,
            fontSize: 13,
            lineNumbers: 'on',
            lineNumbersMinChars: 3,
            folding: true,
            foldingHighlight: true,
            bracketPairColorization: { enabled: true },
            automaticLayout: true,
            tabSize: 2,
            wordWrap: 'on',
            readOnly: disabled,
            domReadOnly: disabled,
            padding: { top: 12, bottom: 12 },
            renderLineHighlight: 'line',
            cursorBlinking: 'smooth',
            smoothScrolling: true,
            formatOnPaste: true,
            formatOnType: true,
            autoClosingBrackets: 'always',
            autoClosingQuotes: 'always',
            quickSuggestions: false,
            suggestOnTriggerCharacters: false,
            parameterHints: { enabled: false },
            scrollbar: {
              vertical: 'auto',
              horizontal: 'auto',
              verticalScrollbarSize: 10,
              horizontalScrollbarSize: 10,
            },
          }}
          theme="vs-light"
          loading={
            <div className="flex items-center justify-center h-full text-sm text-muted-foreground">
              Loading editor...
            </div>
          }
        />
      </div>

      {/* Format Button */}
      {!disabled && (
        <button
          type="button"
          onClick={handleFormat}
          className="absolute top-2 right-2 z-20 px-2 py-1 text-xs bg-slate-100 hover:bg-slate-200 text-slate-600 rounded transition-colors"
          title="Format JSON (Ctrl+Shift+F)"
        >
          Format
        </button>
      )}

      {/* Error Messages */}
      {(error || internalErrors.length > 0) && (
        <div className="mt-1 text-sm text-red-600">
          {error || internalErrors[0]}
          {internalErrors.length > 1 && (
            <span className="text-xs text-red-500 ml-1">
              (+{internalErrors.length - 1} more)
            </span>
          )}
        </div>
      )}
    </div>
  );
}

/**
 * Validates a JSON string
 * @param jsonString - The JSON string to validate
 * @returns Object with isValid boolean and optional error message
 */
export function validateJson(jsonString: string): { isValid: boolean; error?: string } {
  if (!jsonString || jsonString.trim() === '') {
    return { isValid: true }; // Empty is valid (optional field)
  }

  try {
    JSON.parse(jsonString);
    return { isValid: true };
  } catch (e) {
    return {
      isValid: false,
      error: e instanceof Error ? e.message : 'Invalid JSON',
    };
  }
}

/**
 * Safely parse JSON with fallback
 * @param jsonString - The JSON string to parse
 * @param fallback - Fallback value if parsing fails
 * @returns Parsed object or fallback
 */
export function safeParseJson<T = Record<string, unknown>>(
  jsonString: string,
  fallback: T = {} as T
): T {
  if (!jsonString || jsonString.trim() === '') {
    return fallback;
  }

  try {
    return JSON.parse(jsonString) as T;
  } catch {
    return fallback;
  }
}

export default JsonEditor;
